import React, {useContext, useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {
    ILoginFormProps,
    INotificationProps,
    Main,
    StartScreen,
    StartScreenProps,
    TemplateLogin,
} from '../../../patterns';
import {StoreContext} from '../base/root-store';
import {GenericError} from '../base/GenericError';

export const LoginPage = observer((): JSX.Element => {
    const store = useContext(StoreContext).loginViewStore;
    const labelStore = store.labelStore;
    const [phone, setPhone] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const emailOrPhone = store.emailOrPhone;
    const [otp, setOtp] = useState<string>('');

    useEffect(() => {
        store.onEnter();
    }, []);

    if (store.state === 'error') {
        return <Main><GenericError/></Main>;
    }

    let notifications: INotificationProps[] = [];

    if (store.invalid && store.stage === 'phone') {
        notifications.push(
            {
                type: 'error',
                text: labelStore.label('login.invalid.mobile'),
                defaultIsOpen: true,
            },
        );
    } else if (store.invalid && store.stage === 'email' && store.errorReason === 'employee-not-found') {
        notifications.push(
            {
                type: 'error',
                text: labelStore.label('login.invalid.email'),
                defaultIsOpen: true,
            },
        );
    } else if (store.invalid && store.stage === 'email' && store.errorReason === 'otp-wait-period') {
        notifications.push(
            {
                type: 'error',
                text: labelStore.label('login.otp-wait-period'),
                defaultIsOpen: true,
            },
        );
    } else if (store.invalid && store.stage === 'otp') {
        notifications.push(
            {
                type: 'error',
                text: labelStore.label('login.invalid.otp'),
                defaultIsOpen: true,
            },
        );
    } else {
        if (!store.invalid && store.stage === 'otp' && emailOrPhone === '') {
            notifications = [];
        } else if (!store.invalid && store.stage === 'otp' && emailOrPhone.includes('@')) {
            notifications.push(
                {
                    type: 'info',
                    text: labelStore.label('login.otp-info-email'),
                    defaultIsOpen: true,
                },
            );
        } else if (!store.invalid && store.stage === 'otp' && !emailOrPhone.includes('@')) {
            notifications.push(
                {
                    type: 'info',
                    text: labelStore.label('login.otp-info-phone', {'phone-number': emailOrPhone}),
                    defaultIsOpen: true,
                },
            );
        } else {
            notifications = [];
        }
    }

    const startProps = (): StartScreenProps => ({
        title: labelStore.label('login.options'),
        actions: [
            {
                text: labelStore.label('login.email'),
                onClick: async (): Promise<void> => {
                    await store.goToEmailLogin();
                },
            },
            {
                text: labelStore.label('login.mobile'),
                onClick: async (): Promise<void> => {
                    await store.goToPhoneLogin();
                },
            },
        ],
    });

    const phoneStageProps = (): ILoginFormProps => ({
        title: labelStore.label('login.title'),
        fields: [
            {
                autoFocus: true,
                label: labelStore.label('login.mobile'),
                iconLeft: 'mobile-phone',
                id: 'text1',
                name: 'field',
                focusLabel: labelStore.label('login.mobile.focus'),
                type: 'tel',
                value: phone,
                onChange: (value): void => setPhone(value),
                invalid: store.showValidation && store.checkPhoneNumber(phone) !== undefined,
                description: store.showValidation && store.checkPhoneNumber(phone) ? labelStore.label('common.validation.invalid-phone-format') : undefined,
            },
        ],
        button:
            {
                text: labelStore.label('login.next'),
                onClick: async (): Promise<void> => {
                    await store.initiatePhoneLogin(phone);
                },
            },

        onSubmit: async (): Promise<void> => {
            await store.initiatePhoneLogin(phone);
        },
    });

    const emailStageProps = (): ILoginFormProps => ({
        title: labelStore.label('login.title'),
        fields: [
            {
                autoFocus: true,
                label: labelStore.label('login.email'),
                iconLeft: 'mail-closed',
                id: 'text1',
                name: 'field',
                type: 'email',
                value: email,
                invalid: store.invalid,
                onChange: (value): void => setEmail(value),
            },
        ],
        button:
            {
                text: labelStore.label('login.next'),
                onClick: async (): Promise<void> => {
                    await store.initiateEmailLogin(email);
                },
            },

        onSubmit: async (): Promise<void> => {
            await store.initiateEmailLogin(email);
        },
    });

    const otpStageProps = (): ILoginFormProps => ({
        title: labelStore.label('login.title'),
        fields: [
            {
                label: labelStore.label('login.otp'),
                iconLeft: 'key',
                id: 'text2',
                name: 'field',
                type: 'text',
                value: otp,
                invalid: store.invalid,
                onChange: (value): void => setOtp(value),
            },
        ],
        button:
            {
                text: labelStore.label('login.submit'),
                onClick: (): void => {
                    store.completeOtpLogin(emailOrPhone, otp);
                },
            },

        onSubmit: (): void => {
            store.completeOtpLogin(emailOrPhone, otp);
        },
    });

    const getLoginPageContent = (): JSX.Element => {
        switch (store.stage) {
            case 'start':
                return <StartScreen {...startProps()}/>;
            case 'email':
                return <TemplateLogin {...emailStageProps()}/>;
            case 'phone':
                return <TemplateLogin {...phoneStageProps()}/>;
            case 'otp':
                return <TemplateLogin {...otpStageProps()}/>;
        }
    };

    return (
        <Main mobileBackButton={
            {
                text: '',
                onClick: async (): Promise<void> => {
                    await store.goToMobileStartPage();
                },
            }} mobileForwardButton = {(store.stage === 'email' && store.emailOrPhone !== '') || (store.stage === 'phone'&& store.emailOrPhone !== '') ?
            {
                text: '',
                onClick: async (): Promise<void> => {
                    await store.goToOtpPage();
                },
            } : undefined } notifications={notifications} showBackground={true} isLoading={store.state === 'loading'}>
            {getLoginPageContent()}
        </Main>
    );
});
